/**
 * Resource node visuals — low-poly tree clusters for timber, rock
 * outcrops for stone. One Group per node; depleted nodes are removed
 * by syncNodes.
 */
import * as THREE from "three";
import type { ResourceNode } from "../sim/resources";

const trunkMat = new THREE.MeshStandardMaterial({ color: 0x5a4028, roughness: 1 });
const leafMat = new THREE.MeshStandardMaterial({ color: 0x2e5d2a, roughness: 1 });
const rockMat = new THREE.MeshStandardMaterial({ color: 0x8a8a8e, roughness: 0.95 });

function treeCluster(): THREE.Group {
  const g = new THREE.Group();
  const offs: Array<[number, number, number]> = [
    [0, 0, 1],
    [2.2, 0.6, 0.8],
    [-2, -0.8, 1.2],
  ];
  for (const [ox, oz, s] of offs) {
    const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.35 * s, 0.45 * s, 2.4 * s, 6), trunkMat);
    trunk.position.set(ox, 1.2 * s, oz);
    const crown = new THREE.Mesh(new THREE.ConeGeometry(2 * s, 4.5 * s, 7), leafMat);
    crown.position.set(ox, 4.4 * s, oz);
    g.add(trunk, crown);
  }
  return g;
}

function rockOutcrop(): THREE.Group {
  const g = new THREE.Group();
  const rock = new THREE.Mesh(new THREE.DodecahedronGeometry(2.2, 0), rockMat);
  rock.position.y = 1.4;
  rock.scale.set(1, 0.75, 1);
  const shard = new THREE.Mesh(new THREE.DodecahedronGeometry(1.1, 0), rockMat);
  shard.position.set(2.2, 0.8, 1.2);
  g.add(rock, shard);
  return g;
}

export class NodeField {
  readonly group = new THREE.Group();
  private meshes = new Map<number, THREE.Group>();

  constructor(private readonly groundY: (x: number, z: number) => number = () => 0) {}

  add(node: ResourceNode): void {
    const mesh = node.kind === "timber" ? treeCluster() : rockOutcrop();
    mesh.position.set(node.x, this.groundY(node.x, node.z), node.z);
    mesh.rotation.y = (node.id * 1.7) % Math.PI;
    this.group.add(mesh);
    this.meshes.set(node.id, mesh);
  }

  /** Remove meshes for nodes that no longer exist (depleted). */
  sync(nodes: ResourceNode[]): void {
    const alive = new Set(nodes.map((n) => n.id));
    for (const [id, mesh] of this.meshes) {
      if (!alive.has(id)) {
        this.group.remove(mesh);
        this.meshes.delete(id);
      }
    }
    for (const n of nodes) {
      if (!this.meshes.has(n.id)) this.add(n);
    }
  }
}
